import React, { useCallback, useContext, useState } from 'react';
import t from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

// redux
import { editQuizQuestionOption } from 'redux/actions/quizzes';
import { wrapThunkAction } from 'redux/utils';
import { getFlatCourseAliases } from 'redux/selectors/course';

import useQuizModeAndQuestionType from 'quizzes/hooks/use-quiz-mode-and-question-type';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { css } from '@emotion/core';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { primary } from 'styles/global_defaults/colors';
import quizContext, { QuestionContext, SavingIndicator } from '../../context';
import { CellMode } from './table-cell';
import { config } from '../../../../../config/config.json';

type ReadOnlyCellProps = {
  handleSwitch: Function;
};

const editCellStyles = css`
  height: 100%;
  .label-input {
    width: 90%;
    .nv-text-area {
      display: flex;
      .bs4-input-group {
        align-items: center !important;
        textarea {
          background-color: white;
          border: none;
          outline: none;
          padding: 0;
          padding-left: ${halfSpacing}px;
          font-size: 14px;
          padding-top: ${halfSpacing}px;
          padding-bottom: ${halfSpacing}px;
          overflow: hidden;
          height: 100% !important;
          align-content: center;
        }
        textarea::placeholder {
          opacity: 0;
          line-height: 18px;
        }
      }
    }
  }
  .icon-container {
    display: none;
  }

  &:focus-within,
  &:hover {
    .icon-container {
      display: flex;
    }
    .label-input {
      .nv-text-area {
        .bs4-input-group {
          textarea::placeholder {
            opacity: 1;
          }
        }
      }
    }
  }
  &:focus-within {
    border: 1px solid ${primary};
  }
`;

const ReadOnlyCell = (props: ReadOnlyCellProps) => {
  const { handleSwitch } = props;
  const { isEditMode } = useQuizModeAndQuestionType();
  const { responseOptions } = useContext(QuestionContext);
  const { setSavingStatus, savingIndicatorTimeoutRef } = useContext(quizContext);

  const [cellLabel, setCellLabel] = useState(responseOptions[0].optionContent);

  const dispatch = useDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLabelChange = useCallback(debounce((label: string) => {
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    wrapThunkAction(dispatch(editQuizQuestionOption(
      { id: responseOptions[0].id,
        patch: { optionContent: label },
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  }, 500), []);

  if (isEditMode) {
    return (
      <div
        css={editCellStyles}
        className='label-container d-flex w-100'
      >
        <NvTooltip text={t.QUIZZES.TABLE_QUESTION.READ_ONLY_INPUT_LABEL()}>
          <NvTextArea
            className='label-input'
            placeholder={t.QUIZZES.TABLE_QUESTION.INPUT_TEXT_LABEL()}
            onChange={({ target: { value } }) => { setCellLabel(value); handleLabelChange(value); }}
            value={cellLabel}
          />
        </NvTooltip>
        <div className='icon-container ml-auto align-items-center pr-3'>
          <NvTooltip text={cellLabel ? t.QUIZZES.TABLE_QUESTION.CLEAR_INPUT() : t.QUIZZES.TABLE_QUESTION.CELL_SWITCH_TO_ANSWER(courseAliases)}>
            <NvIcon
              className={`${cellLabel ? 'text-gray-3' : 'text-primary'} d-flex align-items-center`}
              icon={cellLabel ? 'close' : 'editable'}
              size='small'
              onClick={() => {
                if (cellLabel) {
                  setCellLabel('');
                  handleLabelChange('');
                } else {
                  handleSwitch(CellMode.ANSWER_MODE);
                }
              }}
              data-qa={config.pendo.activities.quiz.questionModal.tableQuestion.switchToAnswerMode}
            />
          </NvTooltip>
        </div>
      </div>
    );
  }

  return (
    <div className='w-100 h-100 d-flex align-items-center pl-2'>
      {responseOptions[0].optionContent}
    </div>
  );
};

export default ReadOnlyCell;
